import React, { useState, useEffect, useMemo } from "react";
import type { DataProvider } from "react-admin";
import { Admin, Resource, Layout, AppBar } from "react-admin";
import { BrowserRouter as Router, Route, Switch } from "react-router-dom";
import buildGraphQLProvider from "ra-data-graphql";
import {
	ApolloClient,
	createHttpLink,
	InMemoryCache,
	from,
} from "@apollo/client/core";
import { ApolloProvider } from "@apollo/client/react";
import { setContext } from "@apollo/client/link/context";
import { RetryLink } from "@apollo/client/link/retry";
import { Settings, DateTime } from "luxon";
import countryOptions from "~/utils/country-options.ts";
import {
	Assignment,
	AssignmentTurnedIn,
	AssignmentIndSharp,
	People,
	PhotoAlbum,
	Photo,
	Palette,
	LocalAtm,
	LibraryBooks,
	Assessment,
	Subject,
	Tune,
	Brush,
	PhotoLibrary,
	FormatListBulleted as BulkInventoryItemIcon,
	Scanner as BulkInventoryScanIcon,
	DeveloperBoard as DeveloperBoardIcon,
	CreateNewFolder as CreateNewFolderIcon,
	FitnessCenterOutlined,
	Error as ErrorIcon,
	RateReview,
} from "@material-ui/icons";
import { Grid, CircularProgress } from "@material-ui/core";
import {
	SavedProjectList,
	SavedProjectShow,
} from "~/features/saved-project/components.tsx";
import {
	BrickArtDesignList,
	BrickArtDesignShow,
} from "~/features/brick-art-design/components.tsx";
import { ReportList } from "~/features/report/components.tsx";
import {
	CartProjectList,
	CartProjectShow,
} from "~/features/cart-project/components.tsx";
import { Dashboard } from "~/features/dashboard/index.tsx";
import { UserShow, UserList } from "~/features/user/components.tsx";
import buildQuery from "~/api/build-query.ts";
import {
	OrderedProjectList,
	OrderedProjectShow,
} from "~/features/ordered-project/components.tsx";
import {
	PicToBrickCreate,
	PicToBrickEdit,
	PicToBrickList,
} from "~/features/pic-to-brick/pic-components.tsx";
import {
	PicToBrickCategoryCreate,
	PicToBrickCategoryEdit,
	PicToBrickCategoryList,
} from "~/features/pic-to-brick/category-components.tsx";
import {
	PaletteColourCreate,
	PaletteColourEdit,
	PaletteColourList,
} from "~/features/palette-colour/components.tsx";
import { EditPricingModel } from "~/features/pricing-model/components.tsx";
import ManageInventory from "~/features/inventory/manage-inventory.tsx";
import InventoryHistory from "~/features/inventory/inventory-history.tsx";
import InventorySettings from "~/features/inventory/inventory-settings.tsx";
import InventoryItemShow from "~/features/inventory/inventory-item-show.tsx";
import InventoryReport from "~/features/inventory/inventory-report.tsx";
import { PixAPicCodeList } from "~/features/pix-a-pic/code-components.tsx";
import {
	PixAPicCodeGenerationCreate,
	PixAPicCodeGenerationList,
} from "~/features/pix-a-pic/code-generation-components.tsx";
import InventoryReportLegacy from "~/features/inventory-legacy/inventory-report.tsx";
import InventoryItemShowLegacy from "~/features/inventory-legacy/inventory-item-show.tsx";
import InventoryHistoryLegacy from "~/features/inventory-legacy/inventory-history.tsx";
import {
	BulkInventoryItemList,
	CreateBulkInventoryItem,
} from "~/features/inventory-bulk/bulk-inventory-item-components.tsx";
import { BulkInventoryScanList } from "~/features/inventory-bulk/bulk-inventory-scan-components.tsx";
import { CreateBulkInventoryScan } from "~/features/inventory-bulk/scan-create-components.tsx";
import { ReviewBulkInventoryScan } from "~/features/inventory-bulk/scan-review-components.tsx";
import { ShopifyUrlProvider } from "~/hooks/shopify-url.tsx";
import { MediaBaseUrlProvider } from "~/hooks/media-base-url.tsx";
import { BaseplateSizesProvider } from "~/hooks/baseplate-sizes.tsx";
import { InventoryTypesBlacklistProvider } from "~/hooks/inventory.tsx";
import { isFeatureEnabled } from "~/utils/feature-flags.ts";
import { createAuthProvider } from "./auth-provider.ts";
import InventoryWeights from "~/features/inventory-weights/inventory-weights.tsx";
import InventorySkus from "~/features/inventory/inventory-skus/inventory-skus.tsx";
import {
	ImageDetailsList,
	ImageDetailsShow,
} from "~/features/image-details/components.tsx";
import { EditInstructionSettings } from "~/features/instruction-settings/components.tsx";
import XeroAuthCallback from "~/features/inventory/xero-auth-callback.tsx";
import XeroErrors from "~/features/inventory/xero-errors.tsx";
import {
	PixAPicTestimonialEdit,
	PixAPicTestimonialList,
} from "~/features/pix-a-pic/testimonial-components.tsx";
import { CustomMenu } from "./custom-menu.tsx";
import { TimezoneProvider } from "~/hooks/timezone.tsx";

type AppProps = {
	readonly apiUrl: string;
	readonly shopifyUrl: string;
	readonly mediaBaseUrl: string;
	readonly userPoolId: string;
	readonly userPoolClientId: string;
	readonly pixAPicEnabled: boolean;
	readonly inventoryTypesBlacklist: readonly Record<string, unknown>[];
	readonly baseplateSizes: readonly number[];
	readonly inventoryBasePlateSizes: readonly number[];
	readonly siteCode: string;
	readonly availableSites: readonly {
		code: string;
		url: string;
		label: string;
	}[];
	readonly timeZone: string;
	readonly authCookieDomain: string;
};

//wrapper to override default export function for those pages that use it to change dates to passed timezone
function wrapDataProvider(provider: DataProvider): DataProvider {
	return {
		...provider,
		getList: async (resource, params) => {
			const result = await provider.getList(resource, params);
			//only want to process if it's an export so we are looking for large perPage values
			if (params.pagination?.perPage > 750) {
				return {
					...result,
					data: result.data.map((record: any) => {
						const transformed = { ...record };
						Object.entries(record).forEach(([key, value]) => {
							if (typeof value === "string") {
								const date = DateTime.fromISO(value);
								if (date.isValid) {
									transformed[key] = date
										.setZone(Settings.defaultZoneName)
										.toFormat("yyyy-MM-dd HH:mm:ss");
								}
							}
						});
						return transformed;
					}),
				};
			}
			return result;
		},
	};
}

function App({
	apiUrl,
	shopifyUrl,
	mediaBaseUrl,
	baseplateSizes,
	inventoryTypesBlacklist,
	inventoryBasePlateSizes: inventoryBaseplateSizes,
	pixAPicEnabled,
	userPoolId,
	userPoolClientId,
	siteCode,
	availableSites,
	timeZone,
	authCookieDomain,
}: AppProps) {
	const [dataProvider, setDataProvider] = useState<DataProvider | undefined>(
		undefined,
	);

	// Get country data from country-options
	const countryData = useMemo(() => {
		const siteCountryCode = siteCode === "uk" ? "gb" : siteCode;
		const country = countryOptions.find(
			(o) => o.value.toLowerCase() === siteCountryCode.toLowerCase(),
		);
		if (!country) {
			throw new Error(`Invalid site code: ${siteCountryCode}`);
		}
		return country;
	}, [siteCode]);

	// Update document title when siteCode changes
	useEffect(() => {
		document.title = `Brick Me Admin - ${countryData.label}`;
	}, [countryData.label]);

	const authProvider = useMemo(
		() =>
			createAuthProvider({
				userPoolId,
				userPoolClientId,
				cookieDomain: authCookieDomain,
			}),
		[userPoolId, userPoolClientId, authCookieDomain],
	);

	const apolloClient = useMemo(() => {
		const httpLink = createHttpLink({
			uri: apiUrl,
		});

		const authLink = setContext(async (_, { headers }) => {
			if (!authProvider.getIdentity) {
				throw new Error("No way to get identity");
			}

			const adminUser = await authProvider.getIdentity();
			if (!adminUser) {
				return { headers };
			}
			return {
				headers: {
					...headers,
					Authorization: adminUser.token,
				},
			};
		});

		return new ApolloClient({
			link: from([new RetryLink(), authLink, httpLink]),
			cache: new InMemoryCache(),
			defaultOptions: {
				watchQuery: {
					fetchPolicy: "no-cache",
				},
				query: {
					fetchPolicy: "no-cache",
				},
			},
		});
	}, [apiUrl, authProvider]);

	useEffect(() => {
		// eslint-disable-next-line @typescript-eslint/no-floating-promises
		(async () => {
			const provider = await buildGraphQLProvider({
				client: apolloClient as any,
				// Too difficult to keep up to date
				// introspection: { schema } as any,
				buildQuery,
			});

			// This is confusing to me - not sure why this needs to be a function, but buildQuery doesn't
			// work properly without it.  Wrap the provider to handle date formatting in exports
			setDataProvider(() => wrapDataProvider(provider));
		})();
	}, [apolloClient]);

	if (!dataProvider) {
		return (
			<Grid container spacing={2}>
				<Grid item xs={12}>
					<CircularProgress />
				</Grid>
			</Grid>
		);
	}

	return (
		<TimezoneProvider timezone={timeZone}>
			<ShopifyUrlProvider shopifyUrl={shopifyUrl}>
				<MediaBaseUrlProvider mediaBaseUrl={mediaBaseUrl}>
					<ApolloProvider client={apolloClient}>
						<BaseplateSizesProvider
							baseplateSizes={baseplateSizes}
							inventoryBaseplateSizes={inventoryBaseplateSizes}
						>
							<InventoryTypesBlacklistProvider
								inventoryTypesBlacklist={inventoryTypesBlacklist}
							>
								<Router>
									<Switch>
										{/* Custom Route for XeroAuthCallback - NO authentication required */}
										<Route
											exact
											path="/xero-auth-callback"
											component={XeroAuthCallback}
										/>
										<Route path="/">
											<Admin
												title="Brick.me Admin"
												authProvider={authProvider}
												dataProvider={dataProvider}
												dashboard={() => (
													<Dashboard siteTitle={countryData.label} />
												)}
												menu={(props) => (
													<CustomMenu
														{...props}
														regionFlag={countryData.flag}
														siteCode={siteCode}
														availableSites={availableSites}
														backgroundColor={countryData.color}
													/>
												)}
												layout={(props) => (
													<Layout
														{...props}
														style={{ backgroundColor: countryData.color }}
														appBar={(appBarProps) => (
															<AppBar
																{...appBarProps}
																style={{ backgroundColor: countryData.color }}
															></AppBar>
														)}
													/>
												)}
											>
												{(permissions) =>
													[
														<Resource
															key="users"
															name="users"
															list={UserList}
															show={UserShow}
															icon={People}
															options={{ label: "Users" }}
														/>,
														<Resource
															key="savedProjects"
															name="savedProjects"
															list={SavedProjectList}
															show={SavedProjectShow}
															icon={AssignmentIndSharp}
															options={{ label: "Saved projects" }}
														/>,
														<Resource
															key="cartProjects"
															name="cartProjects"
															list={CartProjectList}
															show={CartProjectShow}
															icon={Assignment}
															options={{ label: "Cart projects" }}
														/>,
														<Resource
															key="orderedProjects"
															name="orderedProjects"
															list={OrderedProjectList}
															show={OrderedProjectShow}
															icon={AssignmentTurnedIn}
															options={{ label: "Ordered projects" }}
														/>,
														<Resource
															key="picToBrickCategories"
															name="picToBrickCategories"
															list={PicToBrickCategoryList}
															edit={PicToBrickCategoryEdit}
															create={PicToBrickCategoryCreate}
															icon={PhotoAlbum}
															options={{ label: "Pic to brick categories" }}
														/>,
														<Resource
															key="picsToBrick"
															name="picsToBrick"
															list={PicToBrickList}
															edit={PicToBrickEdit}
															create={PicToBrickCreate}
															icon={Photo}
															options={{ label: "Pics to brick" }}
														/>,
														<Resource
															key="brickArtDesigns"
															name="brickArtDesigns"
															list={BrickArtDesignList}
															show={BrickArtDesignShow}
															icon={Brush}
															options={{ label: "Brick art designs" }}
														/>,
														<Resource
															key="imageDetails"
															name="imageDetails"
															list={ImageDetailsList}
															show={ImageDetailsShow}
															icon={PhotoLibrary}
															options={{ label: "Image details" }}
														/>,
														<Resource
															key="paletteColours"
															name="paletteColours"
															list={PaletteColourList}
															create={PaletteColourCreate}
															edit={PaletteColourEdit}
															icon={Palette}
															options={{ label: "Palette colours" }}
														/>,
														<Resource
															key="pricingModel"
															name="pricingModel"
															list={EditPricingModel}
															icon={LocalAtm}
															options={{ label: "Pricing model" }}
														/>,
														<Resource
															key="instructionSettings"
															name="instructionSettings"
															list={EditInstructionSettings}
															icon={Tune}
															options={{ label: "Instruction settings" }}
														/>,
														<Resource
															key="manageInventory"
															name="manageInventory"
															list={ManageInventory}
															icon={Subject}
															options={{ label: "Manage inventory" }}
														/>,
														<Resource
															key="inventoryWeights"
															name="inventoryWeights"
															list={InventoryWeights}
															icon={FitnessCenterOutlined}
															options={{ label: "Inventory weights" }}
														/>,
														<Resource
															key="inventorySkus"
															name="inventorySkus"
															list={InventorySkus}
															icon={PhotoLibrary}
															options={{ label: "Inventory SKUs" }}
														/>,
														<Resource
															key="inventoryItems"
															name="inventoryItems"
															list={InventoryHistory}
															show={InventoryItemShow}
															icon={LibraryBooks}
															options={{ label: "Inventory history" }}
														/>,
														<Resource
															key="inventoryReports"
															name="inventoryReports"
															list={InventoryReport}
															icon={Assessment}
															options={{ label: "Inventory report" }}
														/>,
														<Resource
															key="inventorySettings"
															name="inventorySettings"
															list={InventorySettings}
															icon={Tune}
															options={{ label: "Inventory settings" }}
														/>,
														<Resource
															key="xeroErrors"
															name="xeroErrors"
															list={XeroErrors}
															icon={ErrorIcon}
															options={{ label: "Xero sync issues" }}
														/>,
														<Resource
															key="inventoryItemsLegacy"
															name="inventoryItemsLegacy"
															list={InventoryHistoryLegacy}
															show={InventoryItemShowLegacy}
															icon={LibraryBooks}
															options={{ label: "Inventory history - legacy" }}
														/>,
														<Resource
															key="inventoryReportsLegacy"
															name="inventoryReportsLegacy"
															list={InventoryReportLegacy}
															icon={Assessment}
															options={{ label: "Inventory reports - legacy" }}
														/>,
														...(isFeatureEnabled("bulkInventory")
															? [
																	<Resource
																		key="bulkInventoryItems"
																		name="bulkInventoryItems"
																		create={CreateBulkInventoryItem}
																		list={BulkInventoryItemList}
																		icon={BulkInventoryItemIcon}
																		options={{
																			label: "Bulk inventory items",
																			permission: "inventory",
																		}}
																	/>,
																	<Resource
																		key="bulkInventoryCounts"
																		name="bulkInventoryCounts"
																		create={CreateBulkInventoryScan}
																		edit={ReviewBulkInventoryScan}
																		list={BulkInventoryScanList}
																		icon={BulkInventoryScanIcon}
																		options={{
																			label: "Bulk inventory counts",
																			permission: "inventory",
																		}}
																	/>,
																]
															: []),
														<Resource
															key="reports"
															name="reports"
															list={ReportList}
															icon={Assessment}
															options={{ label: "Reports" }}
														/>,
														...(pixAPicEnabled
															? [
																	<Resource
																		key="pixAPicCodeGenerations"
																		name="pixAPicCodeGenerations"
																		create={PixAPicCodeGenerationCreate}
																		list={PixAPicCodeGenerationList}
																		icon={CreateNewFolderIcon}
																		options={{
																			label: "Pix-a-pic code generations",
																		}}
																	/>,
																	<Resource
																		key="pixAPicCodes"
																		name="pixAPicCodes"
																		list={PixAPicCodeList}
																		icon={DeveloperBoardIcon}
																		options={{ label: "Pix-a-pic codes" }}
																	/>,
																	<Resource
																		key="pixAPicTestimonials"
																		name="pixAPicTestimonials"
																		list={PixAPicTestimonialList}
																		edit={PixAPicTestimonialEdit}
																		icon={RateReview}
																		options={{
																			label: "Pix-a-pic testimonials",
																		}}
																	/>,
																]
															: []),
													].filter((r) => {
														// Loading
														if (
															permissions.length === 0 ||
															permissions.includes("*")
														) {
															return true;
														}

														const permission = r.props.options?.permission;
														if (!permission) {
															return permissions.includes("*");
														}
														return permissions.includes(permission);
													})
												}
											</Admin>
										</Route>
									</Switch>
								</Router>
							</InventoryTypesBlacklistProvider>
						</BaseplateSizesProvider>
					</ApolloProvider>
				</MediaBaseUrlProvider>
			</ShopifyUrlProvider>
		</TimezoneProvider>
	);
}

export default App;
